// React
import { useRef, useState } from 'react';

// Store
import useProfileStore from '../../../store/profileStore.js';

// Hooks
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Components
import { Box, Button, Divider, IconButton, LinearProgress, Modal, Paper, Stack, Typography } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import AvatarBox from './AvatarBox.js';
import Formular from './Formular.js';
import CustomSnackbar from '../../custom/CustomSnackbar.js';

// Style
import { orange } from '@mui/material/colors/index.js';
import { darkTheme } from '../../../styles/colorSchemes/index.js';

const EditProfile = () => {
	const { editProfile, openEditProfile, setOpenEditProfile, setProfilePicture, profilePictureDB } = useProfileStore();
	const { isDarkMode } = useAppTheme();
	const [failMessage, setFailMessage] = useState('');
	const [openFail, setOpenFail] = useState(false);
	const [loaded, setLoaded] = useState(0);
	const [openSuccess, setOpenSuccess] = useState(false);
	const [successMessage, setSuccessMessage] = useState('');
	const ref = useRef();

	const selectFile = () => {
		ref.current?.click();
	}

	const onDeleteProfilePicture = () => {
		setProfilePicture(undefined);
	};

	const onEditProfileSubmit = () => {
		editProfile(setLoaded).then((res) => {
			if (res.error) {
				setLoaded(0);
				setOpenFail(true);
				setFailMessage(res.error);
				return;
			}
			setLoaded(0);
			setSuccessMessage('Profil erfolgreich aktualisiert!');
			setOpenSuccess(true);
			setTimeout(() => {
				setOpenSuccess(false);
				setOpenEditProfile(false);
			}, 1200);
		});
	};

	const handleFileSelected = (event) => {
		const MAX_FILE_SIZE = 2;
		const file = event.target.files[0];

		if (!file) {
			return;
		} 

		if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
			setOpenFail(true);
			setFailMessage('Die Datei hat den falschen Dateityp!');
			return;
		}

		if (file.size / 1024 / 1000 < MAX_FILE_SIZE) {
			setProfilePicture(file);
			return;
		}
		setOpenFail(true);
		setFailMessage('Die Datei ist zu groß! (max. 2 MB)');
	};

	const primaryColor = isDarkMode ? orange[500] : '#1976d2';

	return (
		<Modal 
			open={ openEditProfile } 
			sx={{ 
				display: 'flex', 
				alignItems: 'center', 
				justifyContent: 'center', 
				p: 2,
				'@media (max-width: 900px)': {
					alignItems: 'flex-start',
					p: 0,
					overflow: 'auto'
				}
			}}
			onClose={ () => {
				setProfilePicture(profilePictureDB);
				setOpenEditProfile(false);
			}}
		>
			<Paper 
				sx={{ 
					width: '90%',
					maxWidth: '1200px',
					maxHeight: '90vh',
					overflow: 'hidden',
					borderRadius: 3,
					boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
					bgcolor: isDarkMode ? darkTheme.accordionBg : undefined,
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					position: 'relative',
					'@media (max-width: 900px)': {
						width: '100%',
						minHeight: '100vh',
						maxHeight: '100vh',
						borderRadius: 0,
						m: 0
					}
				}}
			>
				<CustomSnackbar 
					open={openFail} 
					onClose={() => setOpenFail(false)} 
					message={failMessage} 
					severity="error" 
					autoHideDuration={4000}
				/>

				<CustomSnackbar 
					open={openSuccess} 
					onClose={() => setOpenSuccess(false)} 
					message={successMessage} 
					severity="success" 
					autoHideDuration={3000}
				/>

				{/* Close Button */}
				<IconButton 
					onClick={ () => {
						setProfilePicture(profilePictureDB);
						setOpenEditProfile(false);
					}}
					sx={{ 
						position: 'absolute',
						top: 16,
						right: 16,
						zIndex: 10,
						bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(255, 255, 255, 0.9)',
						color: isDarkMode ? darkTheme.textSecondary : undefined,
						'&:hover': {
							bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.05)',
							transform: 'rotate(90deg)',
							transition: 'transform 0.3s ease'
						}
					}}
				>
					<CloseOutlined />
				</IconButton>

				<input type='file' accept='image/png, image/jpeg' style={{ display: 'none' }} onChange={ handleFileSelected } ref={ ref }/>

				{/* Left Side - Profile Info */}
				<Box 
					sx={{ 
						width: { xs: '100%', md: '35%' },
						background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}55 100%)`,
						p: 4,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 3,
						minHeight: { xs: '300px', md: 'auto' },
						'@media (max-width: 900px)': {
							p: 3,
							gap: 2,
							minHeight: 'auto',
							flexShrink: 0,
							alignItems: 'center',
							textAlign: 'center'
						}
					}}
				>
					<AvatarBox selectFile={selectFile} onDeleteProfilePicture={ onDeleteProfilePicture }/>

					<Box sx={{ textAlign: 'center' }}>
						<Typography 
							variant='h5' 
							fontWeight={600} 
							color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}
							sx={{
								'@media (max-width: 900px)': {
									fontSize: '1.25rem',
									mr: 5
								}
							}}
						>
							Profil bearbeiten
						</Typography>
						<Typography 
							variant='body2' 
							color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}
							sx={{ 
								mt: 1,
								'@media (max-width: 900px)': {
									fontSize: '0.875rem',
									mr: 5
								}
							}}
						>
							Passe deine persönlichen Informationen an
						</Typography>
					</Box>
				</Box>

				{/* Right Side - Form */}
				<Box 
					sx={{ 
						width: { xs: '100%', md: '65%' },
						p: 4,
						overflow: 'auto',
						maxHeight: { xs: 'calc(90vh - 300px)', md: '90vh' },
						'@media (max-width: 900px)': {
							maxHeight: '100%',
							p: 2,
							flex: 1,
							pb: 4,
							boxSizing: 'border-box'
						}
					}}
				>
					<Typography 
						variant='h5' 
						fontWeight={600}
						color={isDarkMode ? darkTheme.textPrimary : undefined}
						sx={{ 
							mb: 3, 
							mt: { xs: 2, md: 0 },
							'@media (max-width: 900px)': {
								fontSize: '1.25rem',
								mb: 2,
								mt: 1
							}
						}}
					>
						Persönliche Daten
					</Typography>

					<Formular/>

					<Divider sx={{ 
						mt: 4, 
						mb: 3,
						'@media (max-width: 900px)': {
							mt: 3,
							mb: 2
						}
					}}/>

					{ loaded > 0 && (
						<Box sx={{ mb: 2 }}> 
							<LinearProgress 
								sx={{ 
									height: 6, 
									borderRadius: 3,
									'& .MuiLinearProgress-bar': {
										bgcolor: primaryColor
									}
								}} 
								variant='determinate' 
								value={ loaded }
							/>
						</Box>
					)}

					{/* Action Button */}
					<Stack 
						direction='row' 
						justifyContent='flex-end'
						sx={{
							'@media (max-width: 900px)': {
								justifyContent: 'stretch',
								width: '100%'
							}
						}}
					>
						<Button 
							variant='contained'
							onClick={ onEditProfileSubmit }
							sx={{ 
								bgcolor: primaryColor,
								textTransform: 'none',
								px: 4,
								py: 1.2,
								borderRadius: 2,
								boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
								'&:hover': { 
									bgcolor: primaryColor,
									filter: 'brightness(0.9)',
									boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)'
								},
								'@media (max-width: 900px)': {
									width: '100%',
									px: 3
								}
							}}
						>
							Aktualisieren
						</Button>
					</Stack>
				</Box>
			</Paper>
		</Modal>
	);
};

export default EditProfile;